/**
 * VoicebotQualitySection Component
 * Displays AI-generated quality analysis for voicebot calls
 */

import { useVoicebotQuality } from '@/hooks/useAnalytics';
import type { AnalyticsDateRangeParams } from '@/types/analytics';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, TrendingUp, AlertCircle, CheckCircle, XCircle, Smile, Meh, Frown } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface VoicebotQualitySectionProps {
  dateRange: AnalyticsDateRangeParams;
}

export function VoicebotQualitySection({ dateRange }: VoicebotQualitySectionProps) {
  const { data, isLoading, error } = useVoicebotQuality(dateRange);

  if (error) {
    return (
      <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
        <p className="text-sm text-destructive">Failed to load voicebot quality analysis: {error.message}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted animate-pulse rounded" />
        <div className="h-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  const summary = data?.summary || {
    totalAnalyses: 0,
    avgSuccessScore: 0,
    outcomeDistribution: { completed: 0, abandoned: 0, failed: 0 },
    sentimentBreakdown: { positive: 0, neutral: 0, negative: 0 },
  };

  // Get top issues (aggregate from all analyses)
  const topIssues = data?.analyses
    ? data.analyses
        .flatMap((analysis) => analysis.key_issues)
        .reduce((acc, issue) => {
          acc[issue] = (acc[issue] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
    : {};

  const topIssuesList = Object.entries(topIssues)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 5);

  // Get top recommendations
  const topRecommendations = data?.analyses
    ? data.analyses
        .flatMap((analysis) => analysis.recommendations)
        .reduce((acc, rec) => {
          acc[rec] = (acc[rec] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
    : {};

  const topRecommendationsList = Object.entries(topRecommendations)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 5);

  const getSentimentIcon = (sentiment: 'positive' | 'neutral' | 'negative') => {
    switch (sentiment) {
      case 'positive':
        return <Smile className="h-4 w-4 text-green-600" />;
      case 'neutral':
        return <Meh className="h-4 w-4 text-yellow-600" />;
      case 'negative':
        return <Frown className="h-4 w-4 text-red-600" />;
    }
  };

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'abandoned':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Avg Success Score */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Avg Success Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{summary.avgSuccessScore.toFixed(1)}</p>
            <Progress value={summary.avgSuccessScore} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">Out of 100</p>
          </CardContent>
        </Card>

        {/* Total Analyses */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Total Analyses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{summary.totalAnalyses}</p>
            <p className="text-xs text-muted-foreground mt-2">AI-analyzed calls</p>
          </CardContent>
        </Card>

        {/* Completion Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {summary.totalAnalyses > 0
                ? ((summary.outcomeDistribution.completed / summary.totalAnalyses) * 100).toFixed(1)
                : '0'}
              %
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {summary.outcomeDistribution.completed} completed calls
            </p>
          </CardContent>
        </Card>

        {/* Positive Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Positive Sentiment</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {summary.totalAnalyses > 0
                ? ((summary.sentimentBreakdown.positive / summary.totalAnalyses) * 100).toFixed(1)
                : '0'}
              %
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {summary.sentimentBreakdown.positive} positive calls
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Outcome Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Outcome Distribution</CardTitle>
          <CardDescription>Breakdown of call outcomes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(summary.outcomeDistribution).map(([outcome, count]) => {
              const percentage = summary.totalAnalyses > 0 ? (count / summary.totalAnalyses) * 100 : 0;
              return (
                <div key={outcome} className="flex items-center gap-3">
                  {getOutcomeIcon(outcome)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{outcome}</span>
                      <span className="text-sm text-muted-foreground">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sentiment Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Sentiment Breakdown</CardTitle>
          <CardDescription>Customer sentiment analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(summary.sentimentBreakdown).map(([sentiment, count]) => {
              const percentage = summary.totalAnalyses > 0 ? (count / summary.totalAnalyses) * 100 : 0;
              return (
                <div key={sentiment} className="flex items-center gap-3">
                  {getSentimentIcon(sentiment as 'positive' | 'neutral' | 'negative')}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{sentiment}</span>
                      <span className="text-sm text-muted-foreground">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top Issues & Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Issues */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Top Issues
            </CardTitle>
            <CardDescription>Most common problems identified</CardDescription>
          </CardHeader>
          <CardContent>
            {topIssuesList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No issues identified</p>
            ) : (
              <ul className="space-y-2">
                {topIssuesList.map(([issue, count], index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">
                      {count}
                    </Badge>
                    <span className="text-sm flex-1">{issue}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Top Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Top Recommendations
            </CardTitle>
            <CardDescription>AI-generated improvement suggestions</CardDescription>
          </CardHeader>
          <CardContent>
            {topRecommendationsList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recommendations available</p>
            ) : (
              <ul className="space-y-2">
                {topRecommendationsList.map(([recommendation, count], index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Badge variant="outline" className="mt-0.5">
                      {count}
                    </Badge>
                    <span className="text-sm flex-1">{recommendation}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
